// react
import React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { Link as RouterLink, useParams } from '@tanstack/react-router';

// material ui icons
import LanRoundedIcon from '@mui/icons-material/LanRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import SsidChartIcon from '@mui/icons-material/SsidChart';

// material ui
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

// shared components
import DateRangeSelector from '@/components/daterange/DateRangeSelector';

// feature-components
// eslint-disable-next-line import/no-restricted-paths
import { getAlarmImpact } from '@/features/energyxplorer/components/alarms/alarmImpact';
// eslint-disable-next-line import/no-restricted-paths
import AlarmImpactSummary from '@/features/energyxplorer/components/alarms/AlarmImpactSummary';

// components
import { getTypeColor, getTypeIcon } from '@/utils/nodeTypeMapper';

// relative
import AlarmTypeCardHeader from '../AlarmTypeCardHeader';
import BaseloadDeviationChart from './highcharts/BaseloadDeviationChart';

function StartTypeAvatar({ type }) {
  return (
    <Avatar
      variant="rounded"
      sx={{
        backgroundColor: getTypeColor(type) || '#555',
        borderRadius: 2,
        color: '#fff',
        fontWeight: 'bold',
        height: 32,
        width: 32,
      }}
    >
      <i
        style={{ fontSize: '16px' }}
        className={`fas ${getTypeIcon(type) || 'fa-circle'}`}
      ></i>
    </Avatar>
  );
}

function extractEnergyTreeIdentifiers(data) {
  if (!data) {
    throw new Error('No data provided');
  }

  console.log('Extracting energy tree identifiers from data:', data);

  const energyTreeId = data.energy_tree_id;
  const energyGroupName = data.energy_tree_name ?? null;

  if (typeof energyTreeId !== 'number') {
    throw new Error('Invalid or missing energy_tree_id');
  }

  const nodeIdsFromTree = Array.isArray(data.energy_tree_nodes)
    ? data.energy_tree_nodes.filter(Boolean)
    : [];

  const alarmDetails = Array.isArray(data.alarm_details)
    ? data.alarm_details
    : [];

  const byNodeId = new Map();
  const seuItems = [];

  // 1️⃣ Seed from alarm_details (contains name + metrics)
  for (const alarm of alarmDetails) {
    // Check if it's an SEU
    if (alarm?.alarm_scope === 'SEU') {
      const seuType = alarm?.seu_type ?? null;
      const compared_value = alarm?.alarm_result?.compared_value ?? null;
      const upper_limit = alarm?.alarm_result?.upper_limit ?? null;
      const lower_limit = alarm?.alarm_result?.lower_limit ?? null;
      const alarm_scope = alarm?.alarm_scope ?? null;
      const comparison_periods =
        alarm?.alarm_result?.timestamp_details?.comparison_periods ?? [];
      const baseline_periods =
        alarm?.alarm_result?.timestamp_details?.baseline_periods ?? [];

      seuItems.push({
        type: 'seu',
        seuType,
        name: seuType || 'Unknown SEU',
        compared_value,
        upper_limit,
        lower_limit,
        alarm_scope,
        comparison_periods,
        baseline_periods,
        monthly_quantity_delta:
          alarm?.alarm_result?.monthly_quantity_delta ?? null,
        monthly_cost: alarm?.alarm_result?.monthly_cost ?? null,
      });
      continue;
    }

    // Handle regular nodes
    const nodeId = alarm?.node_id;
    if (!nodeId) continue;

    const name = alarm?.node_name ?? null;

    const compared_value = alarm?.alarm_result?.compared_value ?? null;
    const upper_limit = alarm?.alarm_result?.upper_limit ?? null;
    const lower_limit = alarm?.alarm_result?.lower_limit ?? null;
    const alarm_scope = alarm?.alarm_scope ?? null;
    const comparison_periods =
      alarm?.alarm_result?.timestamp_details?.comparison_periods ?? [];
    const baseline_periods =
      alarm?.alarm_result?.timestamp_details?.baseline_periods ?? [];

    if (!byNodeId.has(nodeId)) {
      byNodeId.set(nodeId, {
        type: 'node',
        nodeId,
        name,
        compared_value,
        upper_limit,
        lower_limit,
        alarm_scope,
        comparison_periods,
        baseline_periods,
        monthly_quantity_delta:
          alarm?.alarm_result?.monthly_quantity_delta ?? null,
        monthly_cost: alarm?.alarm_result?.monthly_cost ?? null,
      });
      continue;
    }

    // Merge safely (first non-null wins)
    const existing = byNodeId.get(nodeId);

    byNodeId.set(nodeId, {
      type: 'node',
      nodeId,
      name: existing.nodeName ?? name,
      compared_value: existing.compared_value ?? compared_value,
      upper_limit: existing.upper_limit ?? upper_limit,
      lower_limit: existing.lower_limit ?? lower_limit,
      alarm_scope: existing.alarm_scope ?? alarm_scope,
      comparison_periods:
        existing.comparison_periods?.length > 0
          ? existing.comparison_periods
          : comparison_periods,
      baseline_periods:
        existing.baseline_periods?.length > 0
          ? existing.baseline_periods
          : baseline_periods,
      monthly_quantity_delta:
        existing.monthly_quantity_delta ??
        alarm?.alarm_result?.monthly_quantity_delta ??
        null,
      monthly_cost:
        existing.monthly_cost ?? alarm?.alarm_result?.monthly_cost ?? null,
    });
  }

  // 2️⃣ Ensure energy_tree_nodes are represented
  for (const nodeId of nodeIdsFromTree) {
    if (!byNodeId.has(nodeId)) {
      byNodeId.set(nodeId, {
        type: 'node',
        nodeId,
        nodeName: null,
        compared_value: null,
        upper_limit: null,
        lower_limit: null,
        comparison_periods: [],
        baseline_periods: [],
      });
    }
  }

  const nodes = Array.from(byNodeId.values());
  const allItems = [...nodes, ...seuItems];

  return {
    energyGroupName,
    energyTreeId,
    nodes,
    seuItems,
    allItems,
  };
}

const BaseloadDeviation = ({ data, alarm }) => {
  const account = useParams({ strict: false });
  const { t } = useTranslation();
  const { energyTreeId, energyGroupName, allItems } =
    extractEnergyTreeIdentifiers(data);

  const [selectedItem, setSelectedItem] = React.useState(
    allItems?.length > 0 ? allItems[0] : null,
  );

  console.log('alarm details', alarm);

  const impact = getAlarmImpact(data);

  // Calculate default date range: 1 month before alarm creation to alarm creation date
  const defaultFromDate = (() => {
    if (!alarm?.Created) return undefined;
    const date = new Date(alarm.Created);
    date.setMonth(date.getMonth() - 1);
    return date.toISOString();
  })();

  const handleItemClick = (item) => {
    setSelectedItem(item);
  };

  // Group items by alarm_scope
  const groupedItems = React.useMemo(() => {
    const groups = {
      main_meter: [],
      distribution_board: [],
      portfolio_node: [],
      SEU: [],
    };

    allItems?.forEach((item) => {
      const scope = item.alarm_scope?.toLowerCase();
      if (scope === 'mainmeter' || scope === 'main_meter') {
        groups.main_meter.push(item);
      } else if (scope === 'distribution_board') {
        groups.distribution_board.push(item);
      } else if (scope === 'portfolio_node') {
        groups.portfolio_node.push(item);
      } else if (scope?.toLowerCase() === 'seu') {
        groups.SEU.push(item);
      }
    });

    return groups;
  }, [allItems]);

  // Map scope to display label
  const scopeLabels = {
    main_meter: 'Hovedmåler',
    distribution_board: 'Tavler',
    portfolio_node: 'Portfolio',
    SEU: 'SEU',
  };

  // Helper to get avatar type based on item
  const getAvatarType = (item) => {
    if (!item) return null;

    const scope = item.alarm_scope?.toLowerCase();
    if (scope === 'mainmeter' || scope === 'main_meter') {
      return 'Hovedmåler';
    } else if (scope === 'distribution_board') {
      return 'Tavle';
    } else if (scope === 'portfolio_node') {
      return 'Hovedmåler';
    } else if (scope?.toLowerCase() === 'seu') {
      return item.seuType;
    }
    return 'Node';
  };

  const navigateToEnergyTree =
    selectedItem.alarm_scope !== 'portfolio_node'
      ? `/${account?.accountId}/energyxplorer/deepdive/${energyTreeId}/energy-breakdown`
      : `/${account?.accountId}/energyxplorer/portfolio/power/${energyTreeId}/energy-breakdown`;

  const getNodeAnalysisPath = (item) =>
    item.alarm_scope !== 'portfolio_node'
      ? `/${account?.accountId}/energyxplorer/deepdive/${energyTreeId}/energy-analysis`
      : `/${account?.accountId}/energyxplorer/portfolio/power/${energyTreeId}/energy-analysis`;

  return (
    <Stack spacing={2}>
      <Card>
        <AlarmTypeCardHeader
          alarm={alarm}
          title={t(
            `type.${alarm.AlarmType}.subtype.${alarm.AlarmSubtype}.title`,
            { ns: 'alarms', defaultValue: alarm.AlarmType },
          )}
          avatarIcon={<SsidChartIcon />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            {' '}
            {t(
              `type.${alarm.AlarmType}.subtype.${alarm.AlarmSubtype}.details`,
              {
                ns: 'alarms',
                defaultValue:
                  alarm.Details ||
                  alarm.Message ||
                  'No additional details available.',
              },
            )}
          </Typography>
          {impact ? <AlarmImpactSummary impact={impact} /> : null}
        </CardContent>
      </Card>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(12, minmax(0, 1fr))',
          },
        }}
      >
        <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 3' } }}>
          <Card>
            <CardHeader
              title={
                <Box
                  component={RouterLink}
                  to={navigateToEnergyTree}
                  sx={{
                    textDecoration: 'none',
                    display: 'inline-block',
                    '&:hover': {
                      '& .underlined-text': {
                        textDecorationColor: (theme) =>
                          theme.palette.remoni[700],
                      },
                    },
                  }}
                >
                  <LanRoundedIcon
                    fontSize="small"
                    sx={{
                      mr: 1,
                      verticalAlign: 'middle',
                      color: 'text.primary',
                    }}
                  />
                  <Typography
                    variant="subtitle1"
                    component="span"
                    className="underlined-text"
                    sx={{
                      py: 2,
                      color: 'text.primary',
                      textDecoration: 'underline',
                      textDecorationColor: 'text.disabled',
                      textDecorationThickness: '2px',
                      textUnderlineOffset: '4px',
                      transition: 'text-decoration-color 0.2s ease',
                    }}
                  >
                    {energyGroupName}
                  </Typography>
                </Box>
              }
              sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
              slotProps={{
                title: {
                  variant: 'h6',
                },
              }}
            />
            <CardContent sx={{ paddingTop: 0 }}>
              <List>
                {Object.entries(groupedItems).map(([scope, items]) => {
                  if (items.length === 0) return null;

                  return (
                    <React.Fragment key={scope}>
                      <ListSubheader
                        component="div"
                        sx={{
                          backgroundColor: 'transparent',
                          color: 'text.primary',
                          fontWeight: 'bold',
                        }}
                      >
                        {scopeLabels[scope]}
                      </ListSubheader>
                      {items.map((item, index) => {
                        const key =
                          item.type === 'node'
                            ? `node-${item.nodeId}`
                            : `seu-${item.seuType}-${index}`;
                        const label =
                          item.type === 'node'
                            ? (item.name ?? `Node ${item.nodeId}`)
                            : item.name;
                        const isSelected =
                          item.type === selectedItem?.type &&
                          (item.type === 'node'
                            ? item.nodeId === selectedItem?.nodeId
                            : item.seuType === selectedItem?.seuType);

                        return (
                          <ListItem
                            key={key}
                            disablePadding
                            secondaryAction={
                              item.type === 'node' ? (
                                <Tooltip
                                  title={t(
                                    'portfolio.deepdive_alarms.actions.open_node_analysis',
                                    { ns: 'energy-analysis' },
                                  )}
                                >
                                  <IconButton
                                    edge="end"
                                    size="small"
                                    component={RouterLink}
                                    to={getNodeAnalysisPath(item)}
                                    search={{
                                      node: item?.nodeId,
                                      from: defaultFromDate,
                                      to: alarm?.Created,
                                    }}
                                  >
                                    <QueryStatsRoundedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Tooltip
                                  title={t(
                                    'portfolio.deepdive_alarms.actions.open_node_analysis',
                                    { ns: 'energy-analysis' },
                                  )}
                                >
                                  <IconButton
                                    edge="end"
                                    size="small"
                                    component={RouterLink}
                                    to={getNodeAnalysisPath(item)}
                                    search={{
                                      scope: 'seu',
                                      type: item?.seuType,
                                      from: defaultFromDate,
                                      to: alarm?.Created,
                                    }}
                                  >
                                    <QueryStatsRoundedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )
                            }
                          >
                            <ListItemButton
                              selected={isSelected}
                              onClick={() => handleItemClick(item)}
                              sx={{ borderRadius: 2 }}
                            >
                              <ListItemIcon>
                                {scope === 'main_meter' && (
                                  <StartTypeAvatar type="Hovedmåler" />
                                )}
                                {scope === 'distribution_board' && (
                                  <StartTypeAvatar type="Tavle" />
                                )}
                                {scope === 'portfolio_node' && (
                                  <StartTypeAvatar type="Hovedmåler" />
                                )}
                                {scope === 'SEU' && (
                                  <StartTypeAvatar type={item.seuType} />
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={label}
                                slotProps={{
                                  primary: {
                                    variant: 'body2',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  },
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </List>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 9' } }}>
          <Card>
            <CardHeader
              title={selectedItem?.name || 'Energy Group'}
              avatar={
                selectedItem && (
                  <StartTypeAvatar type={getAvatarType(selectedItem)} />
                )
              }
              sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
              action={
                <DateRangeSelector
                  fromKey="electricityAlarmFrom"
                  toKey="electricityAlarmTo"
                  defaultFrom={defaultFromDate}
                  defaultTo={alarm?.Created}
                  showReset={true}
                />
              }
              slotProps={{
                title: {
                  variant: 'h6',
                },
              }}
            />
            <CardContent>
              <BaseloadDeviationChart
                item={selectedItem}
                energyTreeId={energyTreeId}
                account={account}
                alarm={alarm}
                defaultFrom={defaultFromDate}
                defaultTo={alarm?.Created}
              />
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Stack>
  );
};

export default BaseloadDeviation;
